import {
  get,
  media,
  mixin,
  reset,
  responsiveImg,
  toggle,
} from "../../../css/util";
import styled, { css } from "styled-components";

import React from "react";
import imageArrowRight from "./images/arrow-right.png";
import imageBackground from "./images/background.jpg";
import imageBaseline from "./images/baseline.png";

export const Article = styled(function({
  className,
  aside,
  backButton,
  children,
}) {
  return (
    <article className={className}>
      <div className="Content">
        <BackButtonContainer>{backButton}</BackButtonContainer>
        {children}
      </div>
      <aside className="Aside">{aside}</aside>
    </article>
  );
})`
  ${reset()};
  background: ${get("color.lightBlue")};
  align-items: stretch;
  display: flex;
  height: 100%;
  justify-content: stretch;
  position: relative;
  width: 100%;
  > .Content {
    flex: 3;
    overflow: auto;
    position: relative;
  }
  > .Aside {
    ${reset()};
    background: transparent url("${imageBackground}") center center/cover;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    flex: 1;
    padding: 0;
    overflow: auto;
    min-width: 300px;
  }
`;

const BackButtonContainer = styled.div`
  position: absolute;
  z-index: 999;
  top: 0;
  left: 0;
  margin: ${get("spacing.padding")};
`;

export const Header = styled.header`
  background: ${get("color.white")};
  position: relative;
`;

export const Headline = styled(function({ className, icon, children }) {
  return (
    <div className={className}>
      {!!icon && <div className="Icon">{icon}</div>}
      <div className="Title">{children}</div>
    </div>
  );
})`
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 0;
  overflow: hidden;
  padding: ${get("spacing.paddingDouble")} ${get("spacing.padding")};
  text-align: center;

  > .Icon {
    display: inline-block;
    figure img {
      ${responsiveImg(true)};
      height: 92px;
    }
  }
  > .Title {
    text-align: left;
    padding: ${get("spacing.padding")};
  }
`;

export const Title = styled.h1`
  ${reset()};
  ${mixin("font.title", 34, "120%")};
  color: ${get("color.blue")};
  text-transform: uppercase;
  em {
    color: ${get("color.mediumBlue")};
    font-style: normal;
  }
`;

export const Subtitle = styled.h2`
  ${reset()};
  ${mixin("font.subtitle", 20, "120%")};
  color: ${get("color.blue")};
  text-transform: uppercase;
`;

export const Baseline = styled(function({ className, children }) {
  return (
    <div className={className}>
      <span>{children}</span>
    </div>
  );
})`
  ${reset()};
  background: ${get(
    "color.darkBlue",
  )} url("${imageBaseline}") no-repeat center center;
  background-size: 1170px 78px;
  ${mixin("font.title", 20, "100%")};
  color: ${get("color.white")};
  margin: 0;
  padding: ${get("spacing.padding")} ${get("spacing.paddingDouble")};
  text-align: left;
  text-transform: uppercase;
  > span {
    display block;
    margin: 0 auto;
    padding-right: 21.875%;
    max-width: 960px;
  }
`;

export const KeyPoints = styled.div`
  background: ${get("color.blue")};
  color: ${get("color.white")};
  padding: ${get("spacing.paddingDouble")};
`;
export const KeyPointsTitle = styled(function({ className, children }) {
  return (
    <h3 className={className}>
      <span>{children}</span>
    </h3>
  );
})`
  ${mixin("font.subtitle", 18, "20px")};
  color: ${get("color.orange")};
  text-transform: uppercase;
  margin: 0 -${get("spacing.paddingDouble")} ${get("spacing.paddingDouble")} -${get(
      "spacing.paddingDouble",
    )};
  padding: 0 ${get("spacing.paddingDouble")};
  position: relative;

  > span {
    background: ${get("color.blue")};
    margin: 0 -0.5em;
    padding: 0 0.5em;
    position: relative;
  }

  &:before {
    content: "";
    background: ${get("color.darkBlue")};
    height: 16px;
    width: 100%;
    position: absolute;
    left: 0;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
  }
`;
export const KeyPoint = styled(function({ icon, label, className, children }) {
  return (
    <div className={className}>
      <KeyPointIcon src={icon} alt="" />
      <div>
        <KeyPointValue>{children}</KeyPointValue>
        <KeyPointLabel>{label}</KeyPointLabel>
      </div>
    </div>
  );
})`
  display: flex;
  align-items: center;
  justify-content: stretch;
  margin: ${get("spacing.padding")} 0;
`;
const KeyPointIcon = styled.img`
  flex: 0;
  height: 54px;
  margin-left: -10px;
  margin-right: ${get("spacing.padding")};
  width: auto;
`;
const KeyPointValue = styled.span`
  display: block;
  figure img {
    ${responsiveImg(true)};
    height: 48px;
  }
`;
const KeyPointLabel = styled.span`
  ${mixin("font.bold", 16)};
  display: block;
`;

export const Main = styled.main`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  margin: ${get("spacing.paddingDouble")};
`;

export const Blocks = styled.section`
  align-items: stretch;
  display: flex;
  gap: ${get("spacing.padding")};
  flex-direction: row;
  margin: 0 auto;
  max-width: 1280px;
`;

export const Block = styled(function({ image, className, children }) {
  return (
    <div className={className}>
      <BlockImage src={image} />
      <BlockContainer>{children}</BlockContainer>
    </div>
  );
})`
  background: ${get("color.white")};
  margin: 0;
  padding: 0;
`;

const BlockImage = styled.img`
  ${responsiveImg()};
`;
const BlockContainer = styled.div`
  padding: ${get("spacing.padding")};
`;

export const BlockHeadline = styled.h3`
  ${reset()};
  background: transparent url("${imageArrowRight}") no-repeat left -3px;
  background-size: 40px;
  ${mixin("font.title", 18, "120%")};
  color: ${get("color.blue")};
  text-transform: uppercase;
  padding-left: 40px;
  em {
    color: ${get("color.mediumBlue")};
    display: block;
    font-style: normal;
  }
`;
export const BlockList = styled.ul`
  margin: ${get("spacing.padding")} 0;
  padding: 0 0 0 1em;
`;
export const BlockListItem = styled.li`
  list-style: disc;
  margin: 0;
`;

export const Paragraph = styled.p`
  margin: ${get("spacing.padding")} 0;
  padding: 0;
`;

export const Image = styled(function({ className, src, alt, ...props }) {
  return (
    <figure className={className}>
      <img src={src} alt={alt} {...props} />
    </figure>
  );
})`
  ${reset()};
  display: block;
  line-height: 0;
  > img {
    ${responsiveImg()};
  }
`;
